import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import { viewPayment } from "../../Redux/Actions/paymentsActions";
import SearchBar from "../Main/searchBar";
import { ReactComponent as BackIcon } from "../../Assets/Categories/BackIcon.svg";
import "./viewPayment.css";

const ViewPayment = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const paymentState = useSelector((state) => state.payments || {});

  const [isUpdating] = useState(false);

  useEffect(() => {
    if (id) dispatch(viewPayment(id));
    else console.warn("No payment ID found in URL");
  }, [dispatch, id]);

  const data = paymentState.paymentDetail || {};

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "success":
        return "#10b981";
      case "pending":
        return "#f59e0b";
      case "failed":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const formatLabel = (label) => {
    if (!label) return "";
    return label
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Place this helper function in your component or a utils file
  const formatValue = (value) => {
    if (!value) return "";
    return value
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="view-voucher-container">
      <div className="view-voucher-grid padding-20 gap-10 grid-container">
        <div style={{ gridColumn: "3/8" }}>
          <SearchBar style={{ width: "max-content" }} />
        </div>

        <div className="view-voucher-header back-icon margin-0 gap-10 align-center flex-container">
          <Link to="/payments">
            <BackIcon style={{ cursor: "pointer" }} />
          </Link>
          <div className="view-voucher-title-section referral-view">
            <h3 className="view-voucher-main-title">Payment Details</h3>
            <h4 className="view-voucher-breadcrumb">Payments / List / View</h4>
          </div>
        </div>

        <div
          className={`view-voucher-content overall-container ${isUpdating ? "updating" : ""
            }`}
          style={{ background: "white" }}
        >
          <div className="payment-detail-wrapper">
            {/* Main Info Card */}
            <div className="payment-hero-section">
              <div className="payment-header-card">
                <div className="payment-id-section">
                  <span className="payment-label">Payment ID</span>
                  <h1 className="payment-id-value">
                    {data.payment_id || "No Data"}
                  </h1>
                </div>
                <div
                  className="payment-status-badge"
                  style={{
                    backgroundColor: `${getStatusColor(data.payment_status)}15`,
                    border: `2px solid ${getStatusColor(data.payment_status)}`,
                  }}
                >
                  <span
                    className="status-dot"
                    style={{
                      backgroundColor: getStatusColor(data.payment_status),
                    }}
                  ></span>
                  <span
                    className="status-text"
                    style={{ color: getStatusColor(data.payment_status) }}
                  >
                    {data.payment_status || "Unknown"}
                  </span>
                </div>
              </div>

              {/* Amount Display */}
              <div className="payment-amount-card">
                <div className="amount-section">
                  <span className="amount-label">Amount</span>
                  <h2 className="amount-value">
                    {data.currency || ""} {data.amount ?? "0.00"}
                  </h2>
                </div>
                <div className="amount-section">
                  <span className="amount-label">
                    {formatLabel("payment_type")}
                  </span>
                  <h4 className="amount-value">
                    {formatValue(data.payment_type) || "—"}
                  </h4>
                </div>
                {data.is_refund && (
                  <div className="refund-badge">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1L3 6h3v6h4V6h3L8 1z" fill="currentColor" />
                    </svg>
                    Refunded
                  </div>
                )}
              </div>
            </div>

            {/* Details Grid */}
            <div className="payment-details-grid">
              {/* Booking Information */}
              <div className="detail-card">
                <h3 className="card-title">Booking Information</h3>
                <div className="detail-rows">
                  <div className="detail-row">
                    <span className="detail-label">Booking ID</span>
                    <span className="detail-value">
                      {data.booking_id || "—"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Booking Amount</span>
                    <span className="detail-value">
                      {data.booking?.booking_amount
                        ? `${data.booking.booking_amount_currency} ${data.booking.booking_amount}`
                        : "—"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Booking From</span>
                    <span className="detail-value">
                      {formatValue(
                        data.booking?.booking_from ||
                        "—"
                      )}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Payment Method</span>
                    <span className="detail-value">
                      {formatValue(
                        data.booking?.payment_detail?.payment?.entity?.notes?.payment_method ||
                        data.booking?.payment_detail?.payment?.entity?.method ||
                        data.booking?.payment_detail?.type ||
                        "—"
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* User Information */}
              <div className="detail-card">
                <h3 className="card-title">User Information</h3>
                <div className="detail-rows">
                  <div className="detail-row">
                    <span className="detail-label">Name</span>
                    <span className="detail-value">
                      {data.user?.name || "—"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email</span>
                    <span className="detail-value detail-email">
                      {data.user?.email || "—"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Contact Number</span>
                    <span className="detail-value">
                      {data.user?.country_code || ""}{" "}
                      {data.user?.contact_number || "—"}
                    </span>
                  </div>

                  <div className="detail-row profile-image-row">
                    <span className="detail-label">Profile Image</span>
                    {data.user?.profile_image ? (
                      <img
                        src={data.user.profile_image}
                        alt="Profile"
                        className="profile-image"
                      />
                    ) : (
                      <span className="detail-value">No Image</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              {(data.booking?.payment_detail?.payment?.entity?.notes?.payment_method === 'razorpay') &&
                <div className="detail-card">
                  <h3 className="card-title">
                    Payment Information Of Payment GateWay
                  </h3>
                  <div className="detail-rows">
                    <div className="detail-row">
                      <span className="detail-label">Order ID</span>
                      <span className="detail-value">
                        {data.booking?.payment_detail?.payment?.entity?.order_id ||
                          "—"}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Order Amount</span>
                      <span className="detail-value">
                        {data.booking?.payment_detail?.payment?.entity?.amount
                          ? (data.booking.payment_detail.payment.entity.currency?.toUpperCase() || "") + " " + (data.booking.payment_detail.payment.entity.amount / 100)
                          : "—"}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Transaction ID</span>
                      <span className="detail-value">
                        {data.booking?.payment_detail?.payment?.entity?.id || "—"}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Transaction Status</span>
                      <span className="detail-value">
                        {formatValue(data.booking?.payment_detail?.payment?.entity?.status || "—")}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Created At</span>
                      <span className="detail-value">
                        {data.created_at
                          ? new Date(data.created_at).toLocaleString()
                          : "—"}
                      </span>
                    </div>
                  </div>
                </div>
              }

              {/* Additional Details */}
              {data.comment && (
                <div className="detail-card full-width-card">
                  <h3 className="card-title">Comment</h3>
                  <p className="comment-text">{data.comment}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPayment;
